import { useState } from "react";
import Modal from "../../dispatch/actions/Modal";
import type { Deal } from "../../../lib/types";
import { completeDrone } from "../../../lib/droneActions";
import { useAuth } from "../../../context/AuthContext";

interface Props {
  open: boolean;
  deal: Deal;
  onClose: () => void;
  onCompleted: () => void;
}

export default function CompleteDroneModal({
  open,
  deal,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [imgingLink, setImgingLink] = useState(deal.imging_report_link ?? "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    setSubmitting(true);
    try {
      await completeDrone(
        deal,
        { imgingLink: imgingLink.trim(), notes },
        member?.id ?? null,
      );
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Complete drone flight"
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-teal text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Mark complete"}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          IMGING report link <span className="text-slate-400">(optional)</span>
        </label>
        <input
          type="url"
          value={imgingLink}
          onChange={(e) => setImgingLink(e.target.value)}
          placeholder="https://app.imging.com/..."
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
        />
        <p className="text-xs text-slate-500 mt-1">
          Can be added later from Completed tab if not ready yet.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Notes <span className="text-slate-400">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder="Photos captured, anomalies, anything Jeremy should know…"
        />
      </div>

      <p className="text-xs text-slate-500">
        Hands off to Jeremy for report processing (72-hr SLA).
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
